/**
 * Reviews carousel — mobile only.
 *
 * Under 749px the reviews become one panel per screen with dot navigation and a
 * 3-second autoplay. Above that they are three columns already on screen, so the
 * whole thing switches itself off rather than running invisibly.
 *
 * The track is a plain scroll-snap row: swiping is native, and everything here
 * only reads or sets scroll position. With this script absent the reviews are
 * still swipeable — they just lose the dots and the autoplay.
 */

const MOBILE = '(max-width: 749px)';
const INTERVAL = 3000;
// How long to leave autoplay alone after someone swipes or taps a dot. Resuming
// immediately fights the person using it.
const RESUME_AFTER = 8000;

class ReviewsCarousel {
  constructor(section) {
    this.track = section.querySelector('[data-reviews-track]');
    this.dotsWrap = section.querySelector('[data-reviews-dots]');
    if (!this.track || !this.dotsWrap) return;

    this.dots = [...this.dotsWrap.querySelectorAll('[data-reviews-dot]')];
    this.panels = [...this.track.children];
    if (this.panels.length < 2) return;

    this.index = 0;
    this.timer = null;
    this.resumeTimer = null;

    this.mq = window.matchMedia(MOBILE);
    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

    this.dots.forEach((dot, i) => {
      dot.addEventListener('click', () => {
        this.goTo(i);
        this.pauseThenResume();
      });
    });

    this.track.addEventListener('scroll', () => this.syncFromScroll(), { passive: true });

    // A swipe should stop the carousel moving under the reader's thumb.
    this.track.addEventListener('pointerdown', () => this.pauseThenResume(), { passive: true });
    this.track.addEventListener('pointerenter', () => this.stop());
    this.track.addEventListener('pointerleave', () => this.maybeStart());
    this.track.addEventListener('focusin', () => this.stop());

    // Don't animate for someone who is not looking at the tab.
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) this.stop();
      else this.maybeStart();
    });

    this.mq.addEventListener('change', () => this.apply());
    this.apply();
  }

  apply() {
    if (this.mq.matches) {
      this.syncFromScroll();
      this.maybeStart();
    } else {
      this.stop();
      // Leave the desktop grid alone — no inline scroll state to undo, since
      // the track is only scrollable under the media query.
    }
  }

  /** Autoplay only on mobile, and never against a reduced-motion preference. */
  maybeStart() {
    this.stop();
    if (!this.mq.matches || this.reduced.matches || document.hidden) return;
    this.timer = setInterval(() => this.goTo((this.index + 1) % this.panels.length), INTERVAL);
  }

  stop() {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
  }

  pauseThenResume() {
    this.stop();
    if (this.resumeTimer) clearTimeout(this.resumeTimer);
    this.resumeTimer = setTimeout(() => this.maybeStart(), RESUME_AFTER);
  }

  goTo(index) {
    const panel = this.panels[index];
    if (!panel) return;
    this.track.scrollTo({ left: panel.offsetLeft - this.track.offsetLeft, behavior: 'smooth' });
    this.setActive(index);
  }

  /** Derive the index from where the track actually is, so a swipe updates the dots. */
  syncFromScroll() {
    const width = this.track.clientWidth || 1;
    this.setActive(Math.round(this.track.scrollLeft / width));
  }

  setActive(index) {
    const clamped = Math.max(0, Math.min(index, this.panels.length - 1));
    if (clamped === this.index && this.dots[clamped]?.getAttribute('aria-selected') === 'true') return;
    this.index = clamped;
    this.dots.forEach((dot, i) => dot.setAttribute('aria-selected', i === clamped ? 'true' : 'false'));
  }
}

for (const section of document.querySelectorAll('.hiw-reviews')) {
  new ReviewsCarousel(section);
}
